import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Flex, Icon, StatusBadge, Text, Tooltip } from '@driftt/dds'
import { faArrowsRotate } from '@driftt/dds.icons/light/faArrowsRotate'

import { useRefreshingInsightsCount } from 'hooks/useInsightsFeed'
import { FilterPage } from 'utils/filters'
import { formatCompactNumber } from 'utils/formatNumbers'
import { pluralize } from 'utils/formatStrings'

/**
 *
 * @param element {HTMLElement | null} - the element being evaluated to determine if it's in the viewport
 * @returns {boolean} - determines if the element is currently visible in the viewport
 *
 * @example
 *
 * const myRef = useRef<TYPE>(null)
 * const isVisible = useIsInViewPort(myRef.current)
 */
function useIsInViewport(element: HTMLElement | null) {
  // set to true initially for elements that have not rendered yet
  const [isVisible, setIsVisible] = useState<boolean>(true)

  const observer = useMemo(
    () => new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting)),
    []
  )

  useEffect(() => {
    if (element) {
      observer.observe(element)
    }

    return () => {
      observer.disconnect()
    }
  }, [element, observer])

  return isVisible
}

interface RefreshActivityProps {
  mostRecentActivityAt: number
  filterPage: FilterPage
  accountId?: string
  onClick: () => void
  fixedWhenScrolled?: boolean
  showNewButton?: boolean
}

const RefreshActivity = ({
  mostRecentActivityAt,
  filterPage,
  accountId,
  onClick,
  fixedWhenScrolled,
  showNewButton = false
}: RefreshActivityProps) => {
  const numNewerResults = useRefreshingInsightsCount(mostRecentActivityAt, filterPage, accountId)
  const containerRef = useRef<HTMLDivElement>(null)
  const isInViewport = useIsInViewport(containerRef.current)

  if (numNewerResults <= 0) {
    return null
  }

  if (showNewButton) {
    return (
      <Flex
        onClick={onClick}
        alignItems="center"
        justifyContent="center"
        css={{
          width: '$4',
          height: '$4',
          borderRadius: '50%',
          backgroundColor: '$blue8',
          color: '$white',
          position: 'absolute',
          marginLeft: 'auto',
          marginRight: 'auto',
          left: 0,
          right: 0,
          textAlign: 'center',
          top: '$5',
          cursor: 'pointer'
        }}>
        <Tooltip
          style={{
            color: 'var(--drift-dds-colors-gray11)',
            textAlign: 'left'
          }}
          content="Click to refresh the list and view the latest account activity.">
          <Icon icon={faArrowsRotate} size="large" />
        </Tooltip>
      </Flex>
    )
  }

  return (
    <Box
      ref={containerRef}
      css={{
        position: 'relative',
        display: 'block',
        height: '25px',
        width: 'auto',
        zIndex: 'calc($appBar + 1)'
      }}>
      <Box
        css={{
          position: fixedWhenScrolled && !isInViewport ? 'fixed' : 'absolute',
          top: fixedWhenScrolled && !isInViewport ? '28px' : fixedWhenScrolled ? '$1' : 0,
          left: fixedWhenScrolled && !isInViewport ? 'calc(50% + 128px)' : '50%',
          transform: 'translateX(-50%)',
          height: '25px'
        }}>
        <Tooltip side="bottom" content="New activity groups detected">
          <Box
            onClick={onClick}
            css={{
              cursor: 'pointer',
              width: 'max-content'
            }}>
            <StatusBadge variant="pending">
              <Text>
                {`${formatCompactNumber(numNewerResults)}${numNewerResults === 10 ? '+' : ''} ${
                  fixedWhenScrolled ? `new activity ${pluralize('group', numNewerResults)}` : 'new'
                }`}
              </Text>
            </StatusBadge>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default RefreshActivity
